import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Translations } from './i18n/types';

// Import translations as objects instead of JSON files
const enTranslation: Translations = {
  tagline: 'Building Agricultural Bridges',
  nav: {
    services: 'Services',
    team: 'Team',
    contact: 'Contact',
    request: 'Request Equipment',
    imports: 'Imports',
    pricing: 'Pricing',
  },
  hero: {
    title: 'Quality Farm Equipment for Argentina',
    subtitle:
      'Connecting farmers with affordable, high-quality agricultural equipment',
    savingsTitle: 'How we save you an average of 20%',
    savingsButton: 'Learn How We Save You Money',
    cta: 'Request Equipment',
    seeEquipment: 'Equipment for Sale',
    learnImport: 'Import Machinery',
  },
  request: {
    title: 'Request Equipment',
    subtitle: 'Tell us about your equipment needs',
    equipmentType: 'Equipment Type',
    selectType: 'Select equipment type',
    balerLargeSquare: 'Large Square Baler',
    balerSmallSquare2: 'Small Square Baler (2-String)',
    balerSmallSquare3: 'Small Square Baler (3-String)',
    dewpoint: 'DewPoint System',
    planter: 'Planter',
    rake: 'Rake',
    tractor: 'Tractor',
    other: 'Other Equipment',
    otherPlaceholder: "Please specify the equipment you're looking for",
    minBudget: 'Minimum Budget (USD)',
    maxBudget: 'Maximum Budget (USD)',
    targetDate: 'Target Delivery Date',
    details: 'Additional Details',
    detailsPlaceholder: 'Tell us more about your requirements',
    name: 'Your Name',
    namePlaceholder: 'Enter your full name',
    whatsapp: 'WhatsApp Number',
    whatsappPlaceholder: 'Enter your WhatsApp number',
    email: 'Email Address',
    emailPlaceholder: 'Enter your email address',
    phonePlaceholder: 'Phone Number (optional)',
    locationPlaceholder: 'Delivery Location',
    location: 'Delivery Location',
    submit: 'Submit Request',
    submitting: 'Submitting...',
    successMessage: "Thank you! We'll be in touch soon.",
    errorMessage: 'Something went wrong. Please try again.',
  },
  services: {
    title: 'Our Services',
    subtitle:
      'We provide comprehensive solutions for agricultural equipment needs',
    shipping: 'International Shipping',
    shippingDesc:
      'Efficient and reliable shipping to your chosen international port, from US to the world',
    import: 'Import Assistance',
    importDesc: 'Expert guidance through the import process and documentation',
    equipment: 'Equipment Sourcing',
    equipmentDesc:
      'Access to quality agricultural equipment at competitive prices',
    refurbishment: 'Equipment Refurbishment',
    refurbishmentDesc:
      'Professional restoration and upgrading of equipment to meet our rigorous quality standards',
    implementation: 'In Country Implementation',
    implementationDesc:
      'Local support for equipment setup and integration into your operations',
    support: 'Technical Support',
    supportDesc:
      'Expert equipment maintenance and operational support for your machinery',
  },
  team: {
    title: 'Our Team',
    subtitle: 'Meet the people making cross-border agriculture possible',
    russell: 'Russell Mitchell',
    blake: 'Blake Houskeeper',
    juan: 'Juan Cravero',
    eduardo: 'Eduardo González',
    founderRole: 'Founder',
    cofounderRole: 'Co-Founder',
    partnerRole: 'Partner',
    argentinaRepRole: 'Argentina Representative',
    importSpecialist: 'Import Specialist',
    independentImportSpecialist:
      'Independent Import Specialist (Grupolet Company)',
  },
  contact: {
    title: 'Contact Us',
    submit: 'Send Message',
    submitting: 'Sending...',
    successMessage: "Thank you for your message! We'll be in touch soon.",
    errorMessage: 'Failed to send message. Please try again.',
    namePlaceholder: 'Your Name',
    emailPlaceholder: 'Your Email',
    messagePlaceholder: 'Your Message',
    cancel: 'Cancel',
  },
  footer: {
    description:
      'Building bridges in agricultural equipment trade between the US and the world',
    contact: 'Contact Info',
    locations: 'Locations',
    social: 'Follow Us',
    rights: 'All rights reserved.',
    copyright: ' 2024 AgriPuente. All rights reserved.',
  },
  importsPage: {
    title: 'Imports Documentation',
    registerLinkText:
      'To register your company as an importer in Argentina, visit the following link:',
    inquiries: 'For inquiries, contact:',
    quotationExample: 'Sample Quotation',
    item: 'Item',
    amount: 'Amount (USD)',
    fobTotal: 'FOB TOTAL',
    freightTotal: 'TOTAL FREIGHT',
    insuranceTotal: 'TOTAL INSURANCE',
    internalFreight: 'INTERNAL FREIGHT (ADJUSTMENT)',
    customsValue: 'CUSTOMS VALUE',
    comparison: 'Comparison: Capital Good vs. Commercialization',
    capitalGood: 'Capital Good',
    commercialization: 'Commercialization',
    percent: '%',
    currency: 'Currency',
    value: 'Value',
    duties: 'Duties',
    statisticalFee: 'Statistical Fee',
    vatBase: 'VAT Base',
    vat: 'VAT',
    additionalVAT: 'Additional VAT',
    incomeTax: 'Income Tax',
    grossIncome: 'Gross Income',
    total: 'Total:',
    importMachinery: 'Import Machinery into Argentina in 5 Steps',
    importMachineryDescription:
      'The easiest way to import agricultural machinery into Argentina.',
    savingsAmount: 'Save $69,145.40 USD',
    savingsVsText: 'Import as Capital Good vs. Commercialization',
    savingsPercentText: "That's a 47% savings on taxes and fees!",
    savingsLabel: 'SAVINGS:',
  },
  importSteps: {
    step: 'Step {{number}}',
    registerTitle: 'Register as Importer',
    registerDesc: 'Sign up as an importer with AFIP to get started.',
    quotationTitle: 'Request a Quotation',
    quotationDesc:
      'Contact us to receive a detailed quotation for your equipment.',
    approveTitle: 'Approve & Pay',
    approveDesc:
      'Review and approve your quotation. Make payment as instructed. Notify your customs broker.',
    shippingTitle: 'Shipping & Customs',
    shippingDesc: 'We handle shipping and customs clearance for you.',
    receiveTitle: 'Receive Your Machinery',
    receiveDesc: 'Your equipment arrives ready for use in Argentina.',
    goToAfip: 'Go to AFIP',
  },
  pricingPage: {
    modalTitle: 'Enter your name and email to unlock pricing',
    modalButton: 'Unlock Pricing',
    modalPrivacy: "We'll never share your email or send spam.",
    modalEmailPlaceholder: 'you@email.com',
    modalEmailError: 'Please enter a valid email address.',
    modalNamePlaceholder: 'Your Name',
    modalPromoCheckbox:
      'I would like to receive promotional emails about deals or discounts.',
    mainHeading: 'Machinery Pricing & Availability',
    sectionNew: 'New Machinery',
    sectionUsed: 'Used Machinery',
    sectionSourced: 'Sourced Machinery',
    noMachinery: 'No {{type}} machinery found.',
    noMachineryGeneric: 'No machinery found.',
    shipping: 'Shipping:',
    shippingEstimate: 'Estimated shipping: {{estimate}}',
    requestInfo: 'Request Info',
    statusAvailable: 'Available',
    earlyOrderDiscountBanner:
      'Early Order Discount! Get 5% off all purchases before June 1, 2025.',
    earlyOrderDiscountBannerHtml:
      "<strong>Early Order Discount!</strong> Get <span class='text-accent font-bold'>5% off</span> all purchases before <span class='font-bold'>June 1, 2025</span>.",
    earlyOrderDiscountBannerShort:
      '5% Early Order Discount before June 1, 2025!',
    discountEnded: 'Discount ended!',
    timerLeft: 'left',
    priceCurrency: 'USD',
    learnSavingsButton: 'Learn how we can save you an average of 20%',
  },
  testimonials: {
    title: 'What Our Customers Say',
    subtitle: 'Real experiences from farmers who have successfully imported equipment through AgriPuente',
    alexis: {
      quote: 'Working with AgriPuente made importing our DewPoint 6210 system incredibly simple. The entire process was transparent, and we saved thousands compared to buying locally. Russell and his team handled everything - from sourcing to customs clearance.',
      savings: 'Saved over $80,000 USD vs local alternatives'
    }
  },
  partnerships: {
    title: 'Our Trusted Partners',
    subtitle: 'We work with leading manufacturers and service providers to deliver the best equipment and support',
    authorizedDealer: 'Authorized Dealer',
    servicePartner: 'Service Partner',
    dealerDescription: 'Authorized to sell and support premium agricultural equipment',
    serviceDescription: 'Providing local technical support and maintenance services in Argentina',
    trustStatement: 'Our partnerships with industry leaders ensure you receive genuine equipment, expert support, and reliable service throughout your equipment\'s lifetime.'
  },
  warranty: {
    oneYear: '1-Year Warranty',
    included: 'Warranty Included',
    badge: '1 Year'
  },
  importingQuestions: 'Questions About Importing',
  machineInquiry: {
    subject: 'Machine Inquiry',
    name: 'Name',
    brand: 'Brand',
    year: 'Year',
    status: 'Status',
    hours: 'Hours',
    price: 'Price',
    priceCurrency: 'USD',
    na: 'N/A',
  },
};

const esTranslation: Translations = {
  tagline: 'Construyendo Puentes Agrícolas',
  nav: {
    services: 'Servicios',
    team: 'Equipo',
    contact: 'Contacto',
    request: 'Solicitar Equipo',
    imports: 'Importaciones',
    pricing: 'Precios',
  },
  hero: {
    title: 'Equipos Agrícolas de Calidad para Argentina',
    subtitle:
      'Conectando agricultores con equipamiento agrícola asequible y de alta calidad',
    savingsTitle: 'Cómo te ahorramos un promedio del 20%',
    savingsButton: 'Aprende Cómo Te Ahorramos Dinero',
    cta: 'Solicitar Equipo',
    seeEquipment: 'Equipos en Venta',
    learnImport: 'Importar Maquinaria',
  },
  request: {
    title: 'Solicitar Equipo',
    subtitle: 'Cuéntanos sobre tus necesidades de equipo',
    equipmentType: 'Tipo de Equipo',
    selectType: 'Selecciona el tipo de equipo',
    balerLargeSquare: 'Empacadora Cuadrada Grande',
    balerSmallSquare2: 'Empacadora Cuadrada Pequeña (2 Cuerdas)',
    balerSmallSquare3: 'Empacadora Cuadrada Pequeña (3 Cuerdas)',
    dewpoint: 'Sistema Dewpoint',
    planter: 'Sembradora',
    rake: 'Rastrillo',
    tractor: 'Tractor',
    other: 'Otro Equipo',
    otherPlaceholder: 'Por favor, especifica el equipo que buscas',
    minBudget: 'Presupuesto Mínimo (USD)',
    maxBudget: 'Presupuesto Máximo (USD)',
    targetDate: 'Fecha de Entrega Objetivo',
    details: 'Detalles Adicionales',
    detailsPlaceholder: 'Cuéntanos más sobre tus requisitos',
    name: 'Tu Nombre',
    namePlaceholder: 'Ingresa tu nombre completo',
    whatsapp: 'Número de WhatsApp',
    whatsappPlaceholder: 'Ingresa tu número de WhatsApp',
    email: 'Correo Electrónico',
    emailPlaceholder: 'Ingresa tu correo electrónico',
    phonePlaceholder: 'Número de Teléfono (opcional)',
    locationPlaceholder: 'Ubicación de Entrega',
    location: 'Ubicación de Entrega',
    submit: 'Enviar Solicitud',
    submitting: 'Enviando...',
    successMessage: '¡Gracias! Nos pondremos en contacto contigo pronto.',
    errorMessage: 'Algo salió mal. Por favor, inténtalo de nuevo.',
  },
  services: {
    title: 'Nuestros Servicios',
    subtitle:
      'Proporcionamos soluciones integrales para necesidades de equipos agrícolas',
    shipping: 'Envío Internacional',
    shippingDesc:
      'Servicios de envío eficientes y confiables al puerto internacional de tu elección, de EE.UU. al mundo',
    import: 'Asistencia en Importación',
    importDesc:
      'Guía experta a través del proceso de importación y documentación',
    equipment: 'Abastecimiento de Equipos',
    equipmentDesc:
      'Acceso a equipos agrícolas de calidad a precios competitivos',
    refurbishment: 'Restauración de Equipos',
    refurbishmentDesc:
      'Restauración y actualización profesional de equipos para cumplir con nuestros rigurosos estándares de calidad',
    implementation: 'Implementación Local',
    implementationDesc:
      'Soporte local para la configuración e integración del equipo en sus operaciones',
    support: 'Soporte Técnico',
    supportDesc:
      'Soporte experto en mantenimiento y operación de su maquinaria',
  },
  team: {
    title: 'Nuestro Equipo',
    subtitle:
      'Conoce a las personas que hacen posible la agricultura transfronteriza',
    russell: 'Russell Mitchell',
    blake: 'Blake Houskeeper',
    juan: 'Juan Cravero',
    eduardo: 'Eduardo González',
    founderRole: 'Fundador',
    cofounderRole: 'Co-Fundador',
    partnerRole: 'Socio',
    argentinaRepRole: 'Representante en Argentina',
    importSpecialist: 'Especialista en Importaciones',
    independentImportSpecialist:
      'Especialista Independiente en Importaciones (Empresa Grupolet)',
  },
  contact: {
    title: 'Contáctanos',
    submit: 'Enviar Mensaje',
    submitting: 'Enviando...',
    successMessage:
      '¡Gracias por tu mensaje! Nos pondremos en contacto pronto.',
    errorMessage: 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.',
    namePlaceholder: 'Tu Nombre',
    emailPlaceholder: 'Tu Correo Electrónico',
    messagePlaceholder: 'Tu Mensaje',
    cancel: 'Cancelar',
  },
  footer: {
    description:
      'Construyendo puentes en el comercio de equipos agrícolas entre EE.UU. y el mundo',
    contact: 'Información de Contacto',
    locations: 'Ubicaciones',
    social: 'Síguenos',
    rights: 'Todos los derechos reservados.',
    copyright: ' 2024 AgriPuente. Todos los derechos reservados.',
  },
  importsPage: {
    title: 'Documentación de Importaciones',
    registerLinkText:
      'Para registrar su empresa como importador en Argentina, visite el siguiente enlace:',
    inquiries: 'Para consultas, contacte a:',
    quotationExample: 'Ejemplo de Cotización',
    item: 'Concepto',
    amount: 'Monto (USD)',
    fobTotal: 'FOB TOTAL',
    freightTotal: 'FLETE TOTAL',
    insuranceTotal: 'SEGURO TOTAL',
    internalFreight: 'FLETE INTERNO (AJUSTE)',
    customsValue: 'VALOR EN ADUANA',
    comparison: 'Comparativo: Bien de Uso vs. Comercialización',
    capitalGood: 'Bien de Uso',
    commercialization: 'Comercialización',
    percent: '%',
    currency: 'Moneda',
    value: 'Valor',
    duties: 'Derechos',
    statisticalFee: 'Tasa Estadística',
    vatBase: 'Base IVA',
    vat: 'IVA',
    additionalVAT: 'IVA Adicional',
    incomeTax: 'Ganancias',
    grossIncome: 'Ing. Brutos',
    total: 'Total:',
    importMachinery: 'Importe Maquinaria en 5 Pasos',
    importMachineryDescription:
      'El camino más fácil para importar maquinaria agrícola a Argentina.',
    savingsAmount: 'Ahorra $69,145.40 USD',
    savingsVsText: 'Importar como Bien de Uso vs. Comercialización',
    savingsPercentText: '¡Eso es un 47% de ahorro en impuestos y tasas!',
    savingsLabel: 'AHORRO:',
  },
  importSteps: {
    step: 'Paso {{number}}',
    registerTitle: 'Registrarse como Importador',
    registerDesc: 'Inscríbase como importador en AFIP para comenzar.',
    quotationTitle: 'Solicite una Cotización',
    quotationDesc:
      'Contáctenos para recibir una cotización detallada de su equipo.',
    approveTitle: 'Aprobar y Pagar',
    approveDesc:
      'Revise y apruebe su cotización. Realice el pago según las instrucciones. Dar aviso a su Despachante de Aduana.',
    shippingTitle: 'Envío y Aduana',
    shippingDesc: 'Nos encargamos del envío y despacho aduanero por usted.',
    receiveTitle: 'Reciba su Maquinaria',
    receiveDesc: 'Su equipo llega listo para usar en Argentina.',
    goToAfip: 'Ir a AFIP',
  },
  pricingPage: {
    modalTitle: 'Ingresa tu nombre y correo para ver los precios',
    modalButton: 'Ver Precios',
    modalPrivacy: 'Nunca compartiremos su correo ni enviaremos spam.',
    modalEmailPlaceholder: 'usted@email.com',
    modalEmailError: 'Por favor ingrese un correo electrónico válido.',
    modalNamePlaceholder: 'Tu nombre',
    modalPromoCheckbox:
      'Me gustaría recibir correos promocionales sobre ofertas o descuentos.',
    mainHeading: 'Precios y Disponibilidad de Maquinaria',
    sectionNew: 'Maquinaria Nueva',
    sectionUsed: 'Maquinaria Usada',
    sectionSourced: 'Maquinaria Sourcing',
    noMachinery: 'No se encontró maquinaria {{type}}.',
    noMachineryGeneric: 'No se encontró maquinaria.',
    shipping: 'Envío:',
    shippingEstimate: 'Envío estimado: {{estimate}}',
    requestInfo: 'Solicitar información',
    statusAvailable: 'Disponible',
    earlyOrderDiscountBanner:
      '¡Descuento por Pedido Anticipado! Obtén 5% de descuento en todas las compras antes del 1 de junio de 2025.',
    earlyOrderDiscountBannerHtml:
      "<strong>¡Descuento por Pedido Anticipado!</strong> Obtén <span class='text-accent font-bold'>5% de descuento</span> en todas las compras antes del <span class='font-bold'>1 de junio de 2025</span>.",
    earlyOrderDiscountBannerShort:
      '¡5% de descuento anticipado antes del 1 de junio de 2025!',
    discountEnded: '¡Descuento finalizado!',
    timerLeft: 'restantes',
    priceCurrency: 'USD',
    learnSavingsButton: 'Aprende cómo te podemos ahorrar un promedio del 20%',
  },
  testimonials: {
    title: 'Lo Que Dicen Nuestros Clientes',
    subtitle: 'Experiencias reales de agricultores que han importado equipos exitosamente con AgriPuente',
    alexis: {
      quote: 'Trabajar con AgriPuente hizo que importar nuestro sistema DewPoint 6210 fuera increíblemente simple. Todo el proceso fue transparente, y ahorramos miles comparado con comprar localmente. Russell y su equipo se encargaron de todo - desde la búsqueda hasta el despacho aduanero.',
      savings: 'Ahorramos más de $80,000 USD vs alternativas locales'
    }
  },
  partnerships: {
    title: 'Nuestros Socios de Confianza',
    subtitle: 'Trabajamos con fabricantes líderes y proveedores de servicios para entregar el mejor equipo y soporte',
    authorizedDealer: 'Distribuidor Autorizado',
    servicePartner: 'Socio de Servicio',
    dealerDescription: 'Autorizado para vender y brindar soporte a equipos agrícolas premium',
    serviceDescription: 'Brindando soporte técnico local y servicios de mantenimiento en Argentina',
    trustStatement: 'Nuestras alianzas con líderes de la industria aseguran que recibas equipos genuinos, soporte experto y servicio confiable durante toda la vida útil de tu equipo.'
  },
  warranty: {
    oneYear: 'Garantía de 1 Año',
    included: 'Garantía Incluida',
    badge: '1 Año'
  },
  importingQuestions: '¿Preguntas sobre importaciones?',
  machineInquiry: {
    subject: 'Consulta de Máquina',
    name: 'Nombre',
    brand: 'Marca',
    year: 'Año',
    status: 'Estado',
    hours: 'Horas',
    price: 'Precio',
    priceCurrency: 'USD',
    na: 'N/D',
  },
};

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: {
      translation: Translations;
    };
  }
}

const resources = {
  en: {
    translation: enTranslation,
  },
  es: {
    translation: esTranslation,
  },
};

// Detect user language from browser
const detectUserLanguage = (): string => {
  // List of languages our app supports
  const supportedLanguages = ['en', 'es'];

  // Get browser language (primary or full language code like 'en-US')
  const browserLanguage =
    navigator.language || (navigator as any).userLanguage || '';

  // Extract base language code (e.g., 'en' from 'en-US')
  const baseLanguage = browserLanguage.split('-')[0].toLowerCase();

  // Check if the detected language is supported, otherwise use default
  return supportedLanguages.includes(baseLanguage) ? baseLanguage : 'en';
};

i18n.use(initReactI18next).init({
  resources,
  lng: detectUserLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
